import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Brain, Building2, DollarSign, TrendingUp, AlertTriangle,
  CheckCircle, XCircle, RefreshCw, FileText, Target,
  Shield, Zap, ChevronDown, ChevronUp
} from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmt(n) {
  if (!n && n !== 0) return "$0";
  if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(0)}K`;
  return `$${Number(n).toLocaleString()}`;
}

function MetricPill({ label, value, color = "text-white" }) {
  return (
    <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-3 text-center">
      <p className="text-[10px] text-white/30 uppercase tracking-wider mb-1">{label}</p>
      <p className={`font-bold text-lg ${color}`}>{value}</p>
    </div>
  );
}

function RiskBadge({ severity }) {
  const cfg = {
    LOW: "bg-emerald-500/10 border-emerald-500/20 text-[#00FF88]",
    MEDIUM: "bg-amber-500/10 border-amber-500/20 text-[#FF9F0A]",
    HIGH: "bg-red-500/10 border-red-500/20 text-[#FF3B30]",
  };
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${cfg[severity] || cfg.MEDIUM}`}>
      {severity}
    </span>
  );
}

function RecommendationBanner({ recommendation, confidence, score }) {
  const cfg = {
    "APPROVE": { bg: "bg-emerald-500/10", border: "border-emerald-500/30", text: "text-[#00FF88]", icon: CheckCircle },
    "APPROVE WITH CONDITIONS": { bg: "bg-amber-500/10", border: "border-amber-500/30", text: "text-[#FF9F0A]", icon: AlertTriangle },
    "DECLINE": { bg: "bg-red-500/10", border: "border-red-500/30", text: "text-[#FF3B30]", icon: XCircle },
  };
  const c = cfg[recommendation] || cfg["APPROVE WITH CONDITIONS"];
  const Icon = c.icon;
  return (
    <div className={`${c.bg} border ${c.border} rounded-xl p-5 flex items-center justify-between`}>
      <div className="flex items-center gap-4">
        <Icon className={`w-8 h-8 ${c.text}`} />
        <div>
          <p className="text-white/50 text-xs uppercase tracking-wider mb-0.5">Recommendation</p>
          <p className={`text-2xl font-bold ${c.text}`}>{recommendation}</p>
          <p className="text-white/40 text-xs mt-0.5">Confidence: {confidence}</p>
        </div>
      </div>
      <div className="text-center">
        <p className="text-white/30 text-xs uppercase tracking-wider mb-1">Deal Score</p>
        <div className={`text-4xl font-bold ${c.text}`}>{score}</div>
        <p className="text-white/30 text-[10px]">/ 100</p>
      </div>
    </div>
  );
}

export default function AIUnderwritingReport() {
  const [form, setForm] = useState({
    property_address: "",
    arv: "",
    purchase_price: "",
    rehab_estimate: "",
    loan_amount: "",
    borrower_name: "",
    property_type: "SFH",
    exit_strategy: "fix_and_flip",
    borrower_experience_years: "",
    borrower_credit_score: "",
    term_months: "12",
    interest_rate: "",
    notes: "",
  });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showRawForm, setShowRawForm] = useState(true);

  const updateForm = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleGenerate = async () => {
    if (!form.property_address || !form.arv || !form.purchase_price || !form.loan_amount || !form.borrower_name) {
      toast.error("Fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        property_address: form.property_address,
        arv: parseFloat(form.arv),
        purchase_price: parseFloat(form.purchase_price),
        rehab_estimate: parseFloat(form.rehab_estimate) || 0,
        loan_amount: parseFloat(form.loan_amount),
        borrower_name: form.borrower_name,
        property_type: form.property_type,
        exit_strategy: form.exit_strategy,
        borrower_experience_years: form.borrower_experience_years ? parseInt(form.borrower_experience_years) : null,
        borrower_credit_score: form.borrower_credit_score ? parseInt(form.borrower_credit_score) : null,
        term_months: parseInt(form.term_months) || 12,
        interest_rate: form.interest_rate ? parseFloat(form.interest_rate) : null,
        notes: form.notes || null,
      };
      const res = await axios.post(`${BACKEND_URL}/api/portal-tools/underwriting/generate`, payload, { withCredentials: true });
      setReport(res.data);
      setShowRawForm(false);
      toast.success("Underwriting report generated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleNewReport = () => {
    setReport(null);
    setShowRawForm(true);
  };

  return (
    <div className="space-y-6" data-testid="ai-underwriting-report">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-purple-500/10 border border-purple-500/20 rounded-lg flex items-center justify-center">
            <Brain className="w-5 h-5 text-[#BF5AF2]" />
          </div>
          <div>
            <h2 className="text-white font-bold text-base">AI Underwriting Engine</h2>
            <p className="text-white/30 text-xs">Llama 3.3 70B — Full underwriting memo in seconds</p>
          </div>
        </div>
        {report && (
          <button
            onClick={handleNewReport}
            className="flex items-center gap-2 px-4 py-2 border border-white/[0.08] text-white/60 hover:text-white hover:border-white/20 rounded-lg text-sm transition-all"
            data-testid="new-underwriting-btn"
          >
            <RefreshCw className="w-4 h-4" /> New Report
          </button>
        )}
      </div>

      {/* Input Form */}
      {showRawForm && (
        <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-6 space-y-5">
          <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold border-b border-white/[0.06] pb-3">Loan Application Details</p>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label className="text-white/60 text-xs">Property Address *</Label>
              <Input
                value={form.property_address}
                onChange={e => updateForm("property_address", e.target.value)}
                placeholder="123 Main St, Dallas TX 75201"
                className="bg-black border-white/[0.08] text-white"
                data-testid="uw-property-address"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">After Repair Value (ARV) *</Label>
              <Input
                type="number"
                value={form.arv}
                onChange={e => updateForm("arv", e.target.value)}
                placeholder="350000"
                className="bg-black border-white/[0.08] text-white font-mono"
                data-testid="uw-arv"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">Purchase Price *</Label>
              <Input
                type="number"
                value={form.purchase_price}
                onChange={e => updateForm("purchase_price", e.target.value)}
                placeholder="200000"
                className="bg-black border-white/[0.08] text-white font-mono"
                data-testid="uw-purchase-price"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">Rehab Estimate</Label>
              <Input
                type="number"
                value={form.rehab_estimate}
                onChange={e => updateForm("rehab_estimate", e.target.value)}
                placeholder="50000"
                className="bg-black border-white/[0.08] text-white font-mono"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">Loan Amount Requested *</Label>
              <Input
                type="number"
                value={form.loan_amount}
                onChange={e => updateForm("loan_amount", e.target.value)}
                placeholder="175000"
                className="bg-black border-white/[0.08] text-white font-mono"
                data-testid="uw-loan-amount"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">Borrower Name *</Label>
              <Input
                value={form.borrower_name}
                onChange={e => updateForm("borrower_name", e.target.value)}
                placeholder="John Smith / Smith LLC"
                className="bg-black border-white/[0.08] text-white"
                data-testid="uw-borrower-name"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">Property Type</Label>
              <Select value={form.property_type} onValueChange={v => updateForm("property_type", v)}>
                <SelectTrigger className="bg-black border-white/[0.08] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/[0.08]">
                  {["SFH","Multi-Family","Commercial","Condo","Townhouse","Mixed Use"].map(t => (
                    <SelectItem key={t} value={t} className="text-white hover:bg-white/[0.06]">{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">Exit Strategy</Label>
              <Select value={form.exit_strategy} onValueChange={v => updateForm("exit_strategy", v)}>
                <SelectTrigger className="bg-black border-white/[0.08] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/[0.08]">
                  <SelectItem value="fix_and_flip" className="text-white hover:bg-white/[0.06]">Fix and Flip</SelectItem>
                  <SelectItem value="hold" className="text-white hover:bg-white/[0.06]">Buy and Hold</SelectItem>
                  <SelectItem value="brrrr" className="text-white hover:bg-white/[0.06]">BRRRR</SelectItem>
                  <SelectItem value="wholesale" className="text-white hover:bg-white/[0.06]">Wholesale</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">Term (months)</Label>
              <Select value={form.term_months} onValueChange={v => updateForm("term_months", v)}>
                <SelectTrigger className="bg-black border-white/[0.08] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/[0.08]">
                  {["6","9","12","18","24"].map(m => (
                    <SelectItem key={m} value={m} className="text-white hover:bg-white/[0.06]">{m} months</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">Interest Rate (%)</Label>
              <Input
                type="number"
                step="0.25"
                value={form.interest_rate}
                onChange={e => updateForm("interest_rate", e.target.value)}
                placeholder="12.5"
                className="bg-black border-white/[0.08] text-white font-mono"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">Borrower Experience (years)</Label>
              <Input
                type="number"
                value={form.borrower_experience_years}
                onChange={e => updateForm("borrower_experience_years", e.target.value)}
                placeholder="5"
                className="bg-black border-white/[0.08] text-white font-mono"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">Credit Score (approx.)</Label>
              <Input
                type="number"
                value={form.borrower_credit_score}
                onChange={e => updateForm("borrower_credit_score", e.target.value)}
                placeholder="720"
                className="bg-black border-white/[0.08] text-white font-mono"
              />
            </div>

            <div className="col-span-2 space-y-1.5">
              <Label className="text-white/60 text-xs">Additional Notes</Label>
              <Textarea
                value={form.notes}
                onChange={e => updateForm("notes", e.target.value)}
                placeholder="Any additional context about the deal or borrower..."
                className="bg-black border-white/[0.08] text-white min-h-[60px] resize-none"
              />
            </div>
          </div>

          <button
            onClick={handleGenerate}
            disabled={loading}
            data-testid="generate-underwriting-btn"
            className="w-full py-3 bg-[#BF5AF2] hover:bg-[#BF5AF2]/90 disabled:opacity-50 text-white font-bold rounded-lg transition-all flex items-center justify-center gap-2"
          >
            {loading ? (
              <><RefreshCw className="w-4 h-4 animate-spin" /> Generating Underwriting Memo...</>
            ) : (
              <><Brain className="w-4 h-4" /> Generate AI Underwriting Report</>
            )}
          </button>
        </div>
      )}

      {/* Report Output */}
      {report && (
        <div className="space-y-5" data-testid="underwriting-result">
          {/* Recommendation Banner */}
          <RecommendationBanner
            recommendation={report.recommendation}
            confidence={report.confidence}
            score={report.deal_score}
          />

          {/* Executive Summary */}
          <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5">
            <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-2">Executive Summary</p>
            <p className="text-white/70 text-sm leading-relaxed">{report.executive_summary}</p>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-4 gap-3">
            <MetricPill label="LTV" value={`${report.input_metrics?.ltv_pct}%`} color={report.input_metrics?.ltv_pct > 75 ? "text-[#FF9F0A]" : "text-[#00FF88]"} />
            <MetricPill label="LT-ARV" value={`${report.input_metrics?.lt_arv_pct}%`} color={report.input_metrics?.lt_arv_pct > 65 ? "text-[#FF9F0A]" : "text-[#00FF88]"} />
            <MetricPill label="ARV Spread" value={fmt(report.input_metrics?.spread)} color="text-[#D4AF37]" />
            <MetricPill label="Spread %" value={`${report.input_metrics?.spread_pct}%`} color={report.input_metrics?.spread_pct > 20 ? "text-[#00FF88]" : "text-[#FF9F0A]"} />
          </div>

          {/* LTV Analysis + Borrower Profile */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5">
              <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-3 flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5" /> LTV Analysis
              </p>
              <p className="text-white/60 text-sm mb-2">{report.ltv_analysis?.ltv_assessment}</p>
              <p className="text-white/40 text-xs">{report.ltv_analysis?.benchmark}</p>
            </div>
            <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5">
              <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-3 flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5" /> Borrower Profile
              </p>
              <div className="flex items-center gap-2 mb-2">
                <RiskBadge severity={report.borrower_profile?.risk_level || "MEDIUM"} />
              </div>
              <p className="text-white/60 text-sm mb-1">{report.borrower_profile?.experience_assessment}</p>
              <p className="text-white/40 text-xs">{report.borrower_profile?.creditworthiness}</p>
            </div>
          </div>

          {/* Strengths + Risk Factors */}
          <div className="grid grid-cols-2 gap-4">
            {report.strengths?.length > 0 && (
              <div className="bg-emerald-500/[0.04] border border-emerald-500/10 rounded-xl p-5">
                <p className="text-[10px] text-[#00FF88]/60 uppercase tracking-wider font-semibold mb-3 flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5 text-[#00FF88]" /> Strengths
                </p>
                <ul className="space-y-1.5">
                  {report.strengths.map((s, i) => (
                    <li key={i} className="text-white/60 text-sm flex items-start gap-2">
                      <span className="text-[#00FF88] mt-0.5">+</span> {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {report.risk_factors?.length > 0 && (
              <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5">
                <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-3 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> Risk Factors
                </p>
                <ul className="space-y-3">
                  {report.risk_factors.map((r, i) => (
                    <li key={i} className="space-y-1">
                      <div className="flex items-center gap-2">
                        <RiskBadge severity={r.severity} />
                        <span className="text-white/70 text-xs font-medium">{r.factor}</span>
                      </div>
                      {r.mitigation && <p className="text-white/30 text-xs pl-2">→ {r.mitigation}</p>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Suggested Terms */}
          {report.suggested_terms && (
            <div className="bg-[#BF5AF2]/[0.04] border border-[#BF5AF2]/20 rounded-xl p-5">
              <p className="text-[10px] text-[#BF5AF2]/70 uppercase tracking-wider font-semibold mb-4 flex items-center gap-1.5">
                <Target className="w-3.5 h-3.5 text-[#BF5AF2]" /> Suggested Loan Terms
              </p>
              <div className="grid grid-cols-5 gap-3">
                <MetricPill label="Max Loan" value={fmt(report.suggested_terms.max_loan_amount)} color="text-[#BF5AF2]" />
                <MetricPill label="Rate" value={`${report.suggested_terms.suggested_rate_pct}%`} color="text-white" />
                <MetricPill label="Points" value={`${report.suggested_terms.suggested_points}`} color="text-white" />
                <MetricPill label="Term" value={`${report.suggested_terms.suggested_term_months}mo`} color="text-white" />
                <MetricPill label="Reserves" value={`${report.suggested_terms.required_reserves_months}mo`} color="text-[#D4AF37]" />
              </div>
            </div>
          )}

          {/* Conditions if Approved */}
          {report.conditions_if_approved?.length > 0 && (
            <div className="bg-[#0A0A0A] border border-amber-500/20 rounded-xl p-5">
              <p className="text-[10px] text-[#FF9F0A]/70 uppercase tracking-wider font-semibold mb-3 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-[#FF9F0A]" /> Approval Conditions
              </p>
              <ul className="space-y-2">
                {report.conditions_if_approved.map((c, i) => (
                  <li key={i} className="text-white/60 text-sm flex items-start gap-2">
                    <span className="text-[#FF9F0A] font-bold mt-0.5">{i + 1}.</span> {c}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Toggle form to edit */}
          <button
            onClick={() => setShowRawForm(!showRawForm)}
            className="flex items-center gap-2 text-white/30 hover:text-white/60 text-xs transition-colors"
          >
            {showRawForm ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {showRawForm ? "Hide input form" : "Edit deal inputs"}
          </button>

          <p className="text-white/20 text-xs border-t border-white/[0.04] pt-3">
            AI-generated analysis using {report.model}. For informational purposes only. Always conduct your own due diligence before lending.
          </p>
        </div>
      )}
    </div>
  );
}
